'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import type { ApartmentGallerySlide } from '@/lib/apartments/apartmentGallery'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

import { isGalleryInteractiveTarget } from './galleryInteraction'
import { RoomGalleryComparisonSlide } from './RoomGalleryComparisonSlide'
import type { RoomGalleryCopy } from './RoomGalleryComparisonSlide'
import { RoomGalleryImageSlide } from './RoomGalleryImageSlide'

type RoomGalleryCarouselProps = {
  ariaLabel: string
  carouselKey: string
  className?: string
  comparisonCopy: RoomGalleryCopy
  controlLayout?: 'inline' | 'lightbox'
  initialSlide?: number
  items: ApartmentGallerySlide[]
  nextLabel: string
  objectFit?: 'cover' | 'contain'
  onActiveSlideChange?: (index: number) => void
  positionTemplate: string
  previousLabel: string
  resetOnKeyChange?: string
}

export function RoomGalleryCarousel({
  ariaLabel,
  carouselKey,
  className,
  comparisonCopy,
  controlLayout = 'inline',
  initialSlide = 0,
  items,
  nextLabel,
  objectFit = 'cover',
  onActiveSlideChange,
  positionTemplate,
  previousLabel,
  resetOnKeyChange,
}: RoomGalleryCarouselProps) {
  const messages = useMessages()
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [activeSlide, setActiveSlide] = useState(initialSlide)
  const lastReportedIndexRef = useRef(initialSlide)
  const remountKey = resetOnKeyChange ?? carouselKey

  useEffect(() => {
    if (!carouselApi) return

    const onSelect = () => {
      const index = carouselApi.selectedScrollSnap()
      lastReportedIndexRef.current = index
      setActiveSlide(index)
      onActiveSlideChange?.(index)
    }

    onSelect()
    carouselApi.on('select', onSelect)
    carouselApi.on('reInit', onSelect)

    return () => {
      carouselApi.off('select', onSelect)
      carouselApi.off('reInit', onSelect)
    }
  }, [carouselApi, onActiveSlideChange, remountKey])

  useEffect(() => {
    if (!carouselApi) return
    // Skip when Embla already moved here via its own animation (arrows/swipe/keyboard).
    if (initialSlide === lastReportedIndexRef.current) return
    carouselApi.scrollTo(initialSlide, true)
  }, [carouselApi, initialSlide, remountKey])

  const watchDrag = useCallback(
    (_emblaApi: CarouselApi | undefined, event: MouseEvent | TouchEvent) => {
      return !isGalleryInteractiveTarget(event.target)
    },
    [],
  )

  // startIndex is only consumed on Embla init/remount. Keep it out of the memo deps so
  // lifted initialSlide updates from this carousel's own onSelect do not reInit Embla
  // and interrupt in-flight slide animations.
  const carouselOptions = useMemo(
    () => ({
      align: 'start' as const,
      loop: items.length > 1,
      startIndex: initialSlide,
      watchDrag,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remountKey captures startIndex for init
    [items.length, watchDrag, remountKey],
  )

  const positionAnnouncement = positionTemplate
    .replace('{current}', String(activeSlide + 1))
    .replace('{total}', String(items.length))

  const activeItem = items[activeSlide]

  return (
    <div
      className={cn(
        'relative size-full bg-black/10',
        controlLayout === 'inline' ? 'overflow-hidden' : 'overflow-visible bg-transparent',
        className,
      )}
    >
      <Carousel
        key={remountKey}
        aria-label={ariaLabel}
        className="size-full"
        opts={carouselOptions}
        setApi={setCarouselApi}
      >
        <CarouselContent
          className="ml-0 h-full"
          viewportClassName={cn(
            'h-full overflow-x-clip',
            controlLayout === 'lightbox' && 'rounded-[10px] shadow-2xl',
          )}
        >
          {items.map((slide, index) => (
            <CarouselItem
              key={`${carouselKey}-${slide.kind}-${slide.item.id}`}
              aria-hidden={index !== activeSlide ? true : undefined}
              className="relative h-full basis-full pl-0"
              inert={index !== activeSlide ? true : undefined}
            >
              {slide.kind === 'comparison' ? (
                <RoomGalleryComparisonSlide
                  afterLabel={comparisonCopy.afterLabel}
                  beforeLabel={comparisonCopy.beforeLabel}
                  item={slide.item}
                  labelPlacement="below-controls"
                  objectFit={objectFit}
                />
              ) : (
                <RoomGalleryImageSlide item={slide.item} objectFit={objectFit} />
              )}
            </CarouselItem>
          ))}
        </CarouselContent>

        {items.length > 1 && controlLayout === 'inline' ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex min-w-0 items-center justify-center gap-2 overflow-hidden bg-gradient-to-t from-black/75 via-black/35 to-transparent px-3 pb-5 pt-12 sm:gap-3 sm:px-5">
            <CarouselPrevious
              aria-label={previousLabel}
              className="pointer-events-auto static top-auto left-auto size-10 shrink-0 translate-none rounded-full border border-white/95 bg-black/70 text-white hover:bg-black/60 hover:text-white"
              size="icon"
              variant="outline"
            />
            <CarouselDots
              getSlideLabel={(index) =>
                messages.common.goToSlide.replace('{n}', String(index + 1))
              }
            />
            <CarouselNext
              aria-label={nextLabel}
              className="pointer-events-auto static top-auto right-auto size-10 shrink-0 translate-none rounded-full border border-white/95 bg-black/70 text-white hover:bg-black/60 hover:text-white"
              size="icon"
              variant="outline"
            />
          </div>
        ) : null}

        {items.length > 1 && controlLayout === 'lightbox' ? (
          <>
            <CarouselPrevious
              aria-label={previousLabel}
              className="pointer-events-auto -left-12 top-1/2 z-20 hidden size-10 shrink-0 -translate-y-1/2 rounded-full border border-white/95 bg-black/70 text-white hover:bg-black/60 hover:text-white sm:flex"
              size="icon"
              variant="outline"
            />
            <CarouselNext
              aria-label={nextLabel}
              className="pointer-events-auto -right-12 top-1/2 z-20 hidden size-10 shrink-0 -translate-y-1/2 rounded-full border border-white/95 bg-black/70 text-white hover:bg-black/60 hover:text-white sm:flex"
              size="icon"
              variant="outline"
            />
            <div className="pointer-events-none absolute inset-x-0 top-full z-10 mt-3 flex min-w-0 items-center justify-center gap-3">
              <CarouselPrevious
                aria-label={previousLabel}
                className="pointer-events-auto static size-10 shrink-0 translate-none rounded-full border border-white/95 bg-black/70 text-white hover:bg-black/60 hover:text-white sm:hidden"
                size="icon"
                variant="outline"
              />
              <div className="flex min-w-0 items-center justify-center overflow-hidden rounded-full bg-black/70 px-3 py-2">
                <CarouselDots
                  getSlideLabel={(index) =>
                    messages.common.goToSlide.replace('{n}', String(index + 1))
                  }
                />
              </div>
              <CarouselNext
                aria-label={nextLabel}
                className="pointer-events-auto static size-10 shrink-0 translate-none rounded-full border border-white/95 bg-black/70 text-white hover:bg-black/60 hover:text-white sm:hidden"
                size="icon"
                variant="outline"
              />
            </div>
          </>
        ) : null}

        <p className="sr-only" aria-live="polite">
          {positionAnnouncement}
        </p>
      </Carousel>
    </div>
  )
}
